import React, { type RefObject } from 'react'
import { css } from '@linaria/core'
import { ListItem, List, colors } from '@fs/zion-ui'
import { useAutosuggestContext } from './AutosuggestProvider'
import { compareText } from './util'
import { privateProps } from './AutosuggestState'

const matchCss = css`
  span {
    color: ${colors.gray100};
    font-weight: bold;
  }
`

const noOp = (): void => undefined

export function AutosuggestList({ focusedRef }: { focusedRef?: RefObject<HTMLLIElement> }): JSX.Element | null {
  const {
    config: { [privateProps]: api, suggestions, lastResort, isLoading, userInput },
    id,
    highlightMatchingText,
  } = useAutosuggestContext()
  const { focusedIndex } = api

  if (!suggestions?.length && !lastResort && !isLoading) return null

  // TODO warn if list length is over sized
  return (
    <List bleedToContainer id={`listbox-${id}`} role="listbox">
      {suggestions.map((suggestion, suggestionIndex) => {
        const { primaryText, secondaryText, id: suggestionId, key } = suggestion
        return (
          <ListItem
            {...suggestion}
            id={`li-${suggestionIndex}-${id}`}
            key={key || suggestionId || `${primaryText}${secondaryText}`}
            primaryText={highlightMatchingText ? <MatchText input={userInput || ''} text={primaryText} /> : primaryText}
            secondaryText={
              highlightMatchingText ? <MatchText input={userInput || ''} text={secondaryText || ''} /> : secondaryText
            }
            role="option"
            selected={suggestionIndex === focusedIndex}
            ref={suggestionIndex === focusedIndex ? focusedRef : null}
            aria-selected={String(suggestionIndex === focusedIndex)}
            onClick={() => api.select(suggestionIndex)}
            tabIndex={-1} // Not meant to be focusable. We just simulate it.
          />
        )
      })}
      {!!lastResort && (
        <ListItem
          onClick={noOp}
          {...lastResort}
          id={`li-${suggestions.length}-${id}`}
          role="option"
          aria-selected={String(focusedIndex === suggestions.length)}
          aria-live="assertive"
          selected={focusedIndex === suggestions.length}
          tabIndex={-1} // Not meant to be focusable. We just simulate it.
        />
      )}
    </List>
  )
}

function MatchText({ input, text }: { input: string; text: string }): JSX.Element | null {
  if (!text) return null
  const [pre, match, post] = compareText(input, text)
  return (
    <span className={matchCss}>
      {pre}
      {!!match.length && <span>{match}</span>}
      {post}
    </span>
  )
}
